import Icon from '@amzn/meridian/icon';
import { useTheme as useMeridianTheme } from '@amzn/meridian/theme';
import checkIconTokens from '@amzn/meridian-tokens/base/icon/check-small';
import copyIconTokens from '@amzn/meridian-tokens/base/icon/copy';
import { css } from '@emotion/css';
import { accentPressedColor } from '../theme/accent';
import useCopyToClipboard, { describeCopyState } from '../hooks/useCopyToClipboard';
import { createTransition, motionDurations } from '../theme/motion';

export default function CopyableValue({ text }: { text: string }) {
  const meridianTheme = useMeridianTheme();
  const { copyState, copy } = useCopyToClipboard();
  const copyLabel = describeCopyState(copyState, `Copy ${text}`);

  const copyButtonClass = css({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    flex: '0 0 auto',
    padding: 0,
    border: 0,
    borderRadius: 6,
    backgroundColor: 'transparent',
    color: copyState === 'copied' ? meridianTheme('themeSupportInkSuccess') : meridianTheme('themeInkSubdued'),
    cursor: 'pointer',
    opacity: copyState === 'idle' ? 0 : 1,
    transition: createTransition(['opacity', 'background-color', 'color'], motionDurations.instant),
    '&:hover': {
      backgroundColor: accentPressedColor(meridianTheme.mode === 'dark'),
      color: meridianTheme('themeInkDefault'),
    },
    '&:focus-visible': {
      opacity: 1,
      outline: `2px solid ${meridianTheme('themeBorderFocus')}`,
      outlineOffset: 1,
    },
  });
  const valueRowClass = css({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    [`&:hover .${copyButtonClass}`]: { opacity: 1 },
  });
  const valueTextClass = css({
    minWidth: 0,
    overflowWrap: 'anywhere',
  });

  return (
    <span className={valueRowClass}>
      <span className={valueTextClass}>{text}</span>
      <button
        type="button"
        className={copyButtonClass}
        onClick={() => copy(text)}
        aria-label={copyLabel}
        title={copyLabel}
      >
        <Icon tokens={copyState === 'copied' ? checkIconTokens : copyIconTokens} />
      </button>
    </span>
  );
}
